import { apiErrorDetailsOther } from './apiErrorDetailsOther'
import { apiErrorDetailsProcurementReporting } from './apiErrorDetailsProcurementReporting'
import { apiErrorDetailsProjectWorkforce } from './apiErrorDetailsProjectWorkforce'

export type ApiErrorDetail = readonly [code: string, source: string, vi: string, en?: string, fallback?: 'default']
const catalog: readonly ApiErrorDetail[] = (apiErrorDetailsOther as readonly ApiErrorDetail[]).concat(
  apiErrorDetailsProcurementReporting as readonly ApiErrorDetail[],
  apiErrorDetailsProjectWorkforce as readonly ApiErrorDetail[]
)
export const apiErrorDetails: readonly ApiErrorDetail[] = [...new Map(catalog.map(entry => [JSON.stringify([entry[0], entry[1]]), entry])).values()]
type Matcher = { entry: ApiErrorDetail; template: string; pattern?: RegExp; slots: string[] }
const exact = new Map<string, ApiErrorDetail>()
const byCode = new Map<string, Matcher[]>()
const patterns: Matcher[] = []
const escapePattern = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
for (const entry of apiErrorDetails) {
  for (const template of new Set([entry[1], entry[2], entry[3] ?? entry[1]])) {
    const normalized = template.trim()
    const slots = [...normalized.matchAll(/\{(\d+)\}/g)].map(match => match[1])
    if (!slots.length) {
      exact.set(`${entry[0]}\0${normalized}`, entry)
      if (!exact.has(normalized)) exact.set(normalized, entry)
      continue
    }
    const literalLength = template.replace(/\{\d+\}/g, '').trim().length
    if (literalLength < 3) continue
    const matcher: Matcher = { entry, template: normalized, slots }
    // Short row wrappers are safe only when the response carries their matching code.
    if (literalLength >= 8) patterns.push(matcher)
    const list = byCode.get(entry[0]) ?? []
    list.push(matcher)
    byCode.set(entry[0], list)
  }
}
const specificity = (match: Matcher) => match.template.replace(/\{\d+\}/g, '').length
patterns.sort((a, b) => specificity(b) - specificity(a))
for (const list of byCode.values()) list.sort((a, b) => specificity(b) - specificity(a))

export function localizeApiErrorDetail(message: unknown, locale: 'vi' | 'en', code?: string, depth = 0,
  formatParameter?: (value: string, slot: string, source: string) => string): string {
  if (typeof message !== 'string' || !message.trim()) return ''
  const value = message.trim()
  if (depth > 0) {
    const fragments = value.split(/(?<=\.)\s+(?=Row \d+:|Dòng \d+:)/)
    if (fragments.length > 1) return fragments.map(fragment => localizeApiErrorDetail(fragment, locale, code, depth + 1, formatParameter) || fragment).join(' ')
  }
  const literal = (code ? exact.get(`${code}\0${value}`) : undefined) ?? exact.get(value)
  const translated = (entry: ApiErrorDetail) => locale === 'vi' ? entry[2] : entry[3] ?? entry[1]
  if (literal) return translated(literal)
  const groups = code ? [byCode.get(code) ?? [], patterns] : [patterns]
  for (const group of groups) for (const matcher of group) {
    if (code && group === patterns && matcher.entry[0] === code) continue
    matcher.pattern ??= new RegExp(`^${matcher.template.split(/\{\d+\}/).map(escapePattern).join('([\\s\\S]*?)')}$`)
    const match = matcher.pattern.exec(value)
    if (!match) continue
    const args = new Map(matcher.slots.map((slot, index) => [slot, match[index + 1]]))
    return translated(matcher.entry).replace(/\{(\d+)\}/g, (_, slot: string) => {
      const value = args.get(slot) ?? ''
      const nested = depth < 3 && /[.():]/.test(value) ? localizeApiErrorDetail(value, locale, undefined, depth + 1, formatParameter) || value : value
      return formatParameter?.(nested, slot, matcher.entry[1]) ?? nested
    })
  }
  return ''
}

export function apiErrorCodeMessage(code: string, locale: 'vi' | 'en'): string {
  const candidates = apiErrorDetails.filter(entry => entry[0] === code)
  const fallback = candidates.find(entry => entry[4] === 'default')
  if (fallback) return locale === 'vi' ? fallback[2] : fallback[3] ?? fallback[1]
  if (new Set(candidates.map(entry => entry[1])).size !== 1 || !candidates.length || /\{\d+\}/.test(candidates[0][1])) return ''
  return locale === 'vi' ? candidates[0][2] : candidates[0][3] ?? candidates[0][1]
}
